(() => {
  const isDesktop = () => window.matchMedia('(min-width: 782px)').matches;

  const initCollectionProducts = () => {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    const grid = document.querySelector('.babaa-product-grid');
    if (!grid || grid.dataset.productsAnimated) return;
    grid.dataset.productsAnimated = 'true';

    const items = gsap.utils.toArray('.babaa-product-grid__item', grid);
    if (!items.length) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    gsap.registerPlugin(ScrollTrigger);

    const columns = isDesktop() ? 4 : 2;
    const initialVisible = Math.min(items.length, columns);
    const animatedItems = items.slice(initialVisible);

    gsap.set(items.slice(0, initialVisible), { y: 0, opacity: 1 });

    if (!animatedItems.length) return;

    gsap.set(animatedItems, { y: 40, opacity: 0 });
    animatedItems.forEach((item) => item.classList.add('babaa-product-grid__item--animate'));

    const stagger = isDesktop() ? 0.18 : 0.12;

    ScrollTrigger.batch(animatedItems, {
      interval: 0.2,
      batchMax: columns,
      start: 'top 92%',
      once: true,
      onEnter: (batch) => {
        gsap.to(batch, {
          y: 0,
          opacity: 1,
          duration: 0.85,
          stagger,
          ease: 'power2.out',
          overwrite: 'auto',
          onComplete() {
            this.targets().forEach((item) => {
              item.classList.remove('babaa-product-grid__item--animate');
              gsap.set(item, { clearProps: 'transform' });
            });
          },
        });
      },
    });
  };

  const refreshScroll = () => {
    ScrollTrigger?.refresh(true);
  };

  const debounce = (fn, ms) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), ms);
    };
  };

  const initCollectionFilters = (scope = document) => {
    scope.querySelectorAll('[data-babaa-filter-dialog]').forEach((dialog) => {
      if (dialog.dataset.filterInitialized) return;
      dialog.dataset.filterInitialized = 'true';

      const section = dialog.closest('.babaa-collection');
      const openButton = section?.querySelector('[data-babaa-filter-open]');
      const closeButtons = dialog.querySelectorAll('[data-babaa-filter-close]');
      const searchInput = dialog.querySelector('[data-babaa-filter-search]');
      const values = dialog.querySelectorAll('[data-babaa-filter-value]');
      const form = dialog.querySelector('[data-babaa-filter-form]');
      const applyButton = dialog.querySelector('[data-babaa-filter-apply]');
      const countTarget = dialog.querySelector('[data-babaa-filter-product-count]');
      let countController = null;
      let initialProductCount = countTarget?.textContent?.trim() || '';

      const closeDialog = () => {
        restoreFilterState();
        if (countTarget && initialProductCount) countTarget.textContent = initialProductCount;
        countController?.abort();
        openButton?.setAttribute('aria-expanded', 'false');
        dialog.close();
      };

      const checkboxes = dialog.querySelectorAll('.babaa-filter-group input[type="checkbox"]');

      const snapshotFilterState = () => {
        checkboxes.forEach((checkbox) => {
          checkbox.dataset.initialChecked = checkbox.checked ? 'true' : 'false';
        });
        initialProductCount = countTarget?.textContent?.trim() || initialProductCount;
      };

      const restoreFilterState = () => {
        checkboxes.forEach((checkbox) => {
          checkbox.checked = checkbox.dataset.initialChecked === 'true';
        });
      };

      const updateApplyCount = debounce(async () => {
        if (!form || !countTarget || !applyButton) return;

        const sectionId = form.dataset.sectionId;
        if (!sectionId) return;

        countController?.abort();
        countController = new AbortController();
        applyButton.setAttribute('aria-busy', 'true');

        const params = new URLSearchParams(new FormData(form));
        params.set('section_id', sectionId);

        try {
          const response = await fetch(`${form.action}?${params}`, {
            signal: countController.signal,
          });
          if (!response.ok) return;

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const nextCount = doc.querySelector('[data-babaa-filter-product-count]');
          if (nextCount?.textContent != null) {
            countTarget.textContent = nextCount.textContent.trim();
          }
        } catch (error) {
          if (error.name !== 'AbortError') return;
        } finally {
          applyButton.removeAttribute('aria-busy');
        }
      }, 280);

      openButton?.addEventListener('click', () => {
        snapshotFilterState();
        dialog.showModal();
        document.body.classList.add('babaa-filter-open');
        openButton.setAttribute('aria-expanded', 'true');
        updateApplyCount();
      });
      closeButtons.forEach((button) => button.addEventListener('click', closeDialog));
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog();
      });
      dialog.addEventListener('close', () => {
        document.body.classList.remove('babaa-filter-open');
        openButton?.setAttribute('aria-expanded', 'false');
        if (searchInput) searchInput.value = '';
        values.forEach((item) => item.removeAttribute('hidden'));
        openButton?.focus();
      });
      dialog.addEventListener('cancel', () => document.body.classList.remove('babaa-filter-open'));
      searchInput?.addEventListener('input', () => {
        const query = searchInput.value.trim().toLocaleLowerCase();
        values.forEach((item) => {
          const label = item.dataset.babaaFilterValue.toLocaleLowerCase();
          item.toggleAttribute('hidden', query !== '' && !label.includes(query));
        });
      });

      checkboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', updateApplyCount);
      });
    });
  };

  const teardown = () => {
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
  };

  const boot = () => {
    initCollectionFilters();
    initCollectionProducts();
    requestAnimationFrame(refreshScroll);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('load', refreshScroll);

  document.addEventListener('shopify:section:load', (event) => {
    initCollectionFilters(event.target);
    const grid = event.target?.querySelector?.('.babaa-product-grid');
    if (!grid) return;
    delete grid.dataset.productsAnimated;
    teardown();
    boot();
  });
})();
